import React, { useRef, useEffect, useState } from "react";
import styled, { keyframes } from "styled-components";

const SOCIAL_LINKS = [
  { label: "GitHub", url: "https://github.com/jugshaurya" },
  { label: "LinkedIn", url: "https://linkedin.com/in/jugshaurya" },
  { label: "LeetCode", url: "https://leetcode.com/u/jugshaurya" },
  { label: "Twitter", url: "https://twitter.com/jugshaurya" },
  { label: "Blogs", url: "https://notes.jugshaurya.vercel.app" },
];

const Footer: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.2 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <FooterSection ref={ref}>
      <GlowLine $visible={visible} />
      <FooterInner $visible={visible}>
        <TopRow>
          <BigText>Let's build something great.</BigText>
          <ContactCTA href="mailto:shauryasinghal84@gmail.com">
            Get in touch <Arrow>→</Arrow>
          </ContactCTA>
        </TopRow>

        <Divider />

        <BottomRow>
          <Left>
            <Logo>SHAURYA SINGHAL</Logo>
            <Sub>Software Engineer · India</Sub>
          </Left>

          <SocialRow>
            {SOCIAL_LINKS.map((s) => (
              <SocialLink key={s.label} href={s.url} target="_blank" rel="noopener noreferrer">
                {s.label}
              </SocialLink>
            ))}
          </SocialRow>

          <Right>
            <Sub>&copy; {new Date().getFullYear()} · Made with passion</Sub>
          </Right>
        </BottomRow>
      </FooterInner>
      <ScanLine />
    </FooterSection>
  );
};

export default Footer;

/* ── Animations ── */
const scan = keyframes`
  from { left: -30%; }
  to { left: 130%; }
`;

const lineExpand = keyframes`
  from { transform: scaleX(0); }
  to { transform: scaleX(1); }
`;

/* ── Styled ── */
const FooterSection = styled.footer`
  position: relative;
  padding: 3rem 2rem 2rem;
  background: #141317;
  overflow: hidden;
  cursor: none;
`;

const GlowLine = styled.div<{ $visible: boolean }>`
  position: absolute;
  top: 0;
  left: 10%;
  right: 10%;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(139, 92, 246, 0.3), rgba(236, 72, 153, 0.2), transparent);
  transform-origin: center;
  transform: scaleX(${({ $visible }) => ($visible ? 1 : 0)});
  transition: transform 1.2s cubic-bezier(0.16, 1, 0.3, 1);
`;

const ScanLine = styled.div`
  position: absolute;
  top: 0;
  left: -30%;
  width: 30%;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(139, 92, 246, 0.15), transparent);
  animation: ${scan} 6s ease-in-out infinite;
`;

const FooterInner = styled.div<{ $visible: boolean }>`
  max-width: 1100px;
  margin: 0 auto;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transform: translateY(${({ $visible }) => ($visible ? 0 : "20px")});
  transition: opacity 0.8s ease, transform 0.8s ease;
`;

const TopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const BigText = styled.h3`
  font-family: "Cinzel", serif;
  font-size: clamp(1.2rem, 2.5vw, 1.8rem);
  font-weight: 700;
  color: #e0d8e8;
  line-height: 1.3;
`;

const Arrow = styled.span`
  display: inline-block;
  transition: transform 0.3s ease;
`;

const ContactCTA = styled.a`
  font-family: "JetBrains Mono", monospace;
  font-size: 0.7rem;
  color: #8b5cf6;
  text-decoration: none;
  letter-spacing: 0.08em;
  padding: 0.6rem 1.2rem;
  border: 1px solid rgba(139, 92, 246, 0.2);
  border-radius: 100px;
  cursor: none;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(139, 92, 246, 0.08);
    border-color: rgba(139, 92, 246, 0.4);
    color: #a78bfa;
    ${Arrow} { transform: translateX(4px); }
  }
`;

const Divider = styled.div`
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.05), transparent);
  margin-bottom: 1.5rem;
`;

const BottomRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
  }
`;

const Left = styled.div``;
const Right = styled.div``;

const Logo = styled.div`
  font-family: "Cinzel", serif;
  font-size: 0.72rem;
  font-weight: 700;
  color: #a098a8;
  letter-spacing: 0.15em;
  margin-bottom: 0.15rem;
`;

const Sub = styled.p`
  font-family: "JetBrains Mono", monospace;
  font-size: 0.5rem;
  color: #4a3a5a;
  letter-spacing: 0.05em;
`;

const SocialRow = styled.div`
  display: flex;
  gap: 0.3rem;
  flex-wrap: wrap;
  justify-content: center;
`;

const SocialLink = styled.a`
  font-family: "JetBrains Mono", monospace;
  font-size: 0.55rem;
  color: #5a4a6a;
  letter-spacing: 0.08em;
  padding: 0.35rem 0.7rem;
  border-radius: 100px;
  text-decoration: none;
  cursor: none;
  transition: all 0.3s ease;

  &:hover {
    color: #e0d8e8;
    background: rgba(139, 92, 246, 0.08);
    box-shadow: 0 0 15px rgba(139, 92, 246, 0.1);
    transform: translateY(-2px);
  }
`;
